import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchIncidentNotesRange } from '../firebase/firestoreService';
import { getCurrentUser, getUserRole } from '../firebase/authService';
import { canAccessReports, getServiceType } from '../utils/roles';
import { formatDate } from '../utils/dateUtils';
import '../styles/IncidentReports.css';

const TIMEFRAMES = [
  { id: 'week', label: 'Past Week' },
  { id: 'month', label: 'Past Month' },
  { id: 'quarter', label: 'Past Quarter (3 Months)' },
  { id: 'semi-annual', label: 'Past 6 Months' },
  { id: 'annual', label: 'Past Year (12 Months)' },
];

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'incidents', label: 'Incidents' },
  { id: 'clearance', label: 'All-clear' },
];

const toDateStr = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const getServiceLabel = (id) => {
  if (!id || id === 'general') return 'General';
  const s = getServiceType(id);
  return s ? s.name : id;
};

const getRange = (timeframe) => {
  const end = new Date();
  end.setHours(23, 59, 59, 999);
  const start = new Date();
  switch (timeframe) {
    case 'week': start.setDate(start.getDate() - 7); break;
    case 'month': start.setMonth(start.getMonth() - 1); break;
    case 'quarter': start.setMonth(start.getMonth() - 3); break;
    case 'semi-annual': start.setMonth(start.getMonth() - 6); break;
    case 'annual': start.setFullYear(start.getFullYear() - 1); break;
    default: start.setDate(start.getDate() - 7);
  }
  start.setHours(0, 0, 0, 0);
  return { start, end };
};

const isClearanceNote = (note) => !!note.noteText && note.noteText.startsWith('✅ No incident');

const IncidentReports = () => {
  const navigate = useNavigate();
  const user = getCurrentUser();

  const [authChecked, setAuthChecked] = useState(false);
  const [timeframe, setTimeframe] = useState('week');
  const [filterMode, setFilterMode] = useState('all');
  const [serviceFilter, setServiceFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    getUserRole(user.uid).then((role) => {
      if (!canAccessReports(role)) {
        navigate('/dashboard');
      } else {
        setAuthChecked(true);
      }
    });
  }, [user, navigate]);

  useEffect(() => {
    if (!authChecked) return;
    let cancelled = false;
    setLoading(true);
    setError('');
    const { start, end } = getRange(timeframe);
    fetchIncidentNotesRange(toDateStr(start), toDateStr(end))
      .then((data) => {
        if (cancelled) return;
        setNotes(data);
      })
      .catch((err) => {
        console.error('Failed to load incident reports:', err);
        if (!cancelled) setError('Could not load incident reports.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [authChecked, timeframe]);

  const filteredNotes = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return notes.filter((n) => {
      if (filterMode === 'incidents' && isClearanceNote(n)) return false;
      if (filterMode === 'clearance' && !isClearanceNote(n)) return false;
      if (serviceFilter !== 'all' && (n.serviceType || 'general') !== serviceFilter) return false;
      if (term) {
        const haystack = [
          n.noteText,
          n.createdByName,
          n.serviceType,
          getServiceLabel(n.serviceType),
        ].filter(Boolean).join(' ').toLowerCase();
        if (!haystack.includes(term)) return false;
      }
      return true;
    });
  }, [notes, filterMode, serviceFilter, searchTerm]);

  const stats = useMemo(() => {
    const incidents = notes.filter((n) => !isClearanceNote(n)).length;
    const clearances = notes.length - incidents;
    const authors = new Set(notes.map((n) => n.createdByName).filter(Boolean));
    const services = new Set(notes.map((n) => n.serviceType || 'general'));
    return { total: notes.length, incidents, clearances, authors: authors.size, services: services.size };
  }, [notes]);

  const exportCSV = () => {
    if (!filteredNotes.length) return;
    const headers = ['Date', 'Service', 'Logged By', 'Logged At', 'Type', 'Note'];
    const rows = filteredNotes.map((n) => [
      n.serviceDate || '',
      getServiceLabel(n.serviceType),
      n.createdByName || 'Unknown',
      n.createdAt ? formatDate(n.createdAt, 'yyyy-MM-dd HH:mm') : '',
      isClearanceNote(n) ? 'All-clear' : 'Incident',
      (n.noteText || '').replace(/"/g, '""'),
    ]);
    const csv = [headers, ...rows].map((r) => r.map((c) => `"${c}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `incident-reports-${timeframe}-${toDateStr(new Date())}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportPDF = () => {
    if (!filteredNotes.length) return;
    const win = window.open('', '_blank');
    if (!win) {
      setError('Please allow pop-ups to generate the PDF.');
      return;
    }
    const escape = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    }[c]));
    const tfLabel = TIMEFRAMES.find((t) => t.id === timeframe)?.label || timeframe;
    const { start, end } = getRange(timeframe);
    const filterDescription = [
      filterMode !== 'all' ? `Type: ${FILTERS.find(f => f.id === filterMode)?.label}` : null,
      serviceFilter !== 'all' ? `Service: ${getServiceLabel(serviceFilter)}` : null,
      searchTerm.trim() ? `Search: "${searchTerm.trim()}"` : null,
    ].filter(Boolean).join(' · ');

    const rowsHtml = filteredNotes.map((n) => {
      const clearance = isClearanceNote(n);
      return `
        <tr class="${clearance ? 'row-clear' : 'row-incident'}">
          <td class="cell-nowrap">${escape(n.serviceDate || '—')}</td>
          <td><span class="tag tag-${escape(n.serviceType || 'general')}">${escape(getServiceLabel(n.serviceType))}</span></td>
          <td><span class="type type-${clearance ? 'clear' : 'incident'}">${clearance ? 'All-clear' : 'Incident'}</span></td>
          <td class="cell-note">${escape(n.noteText || '')}</td>
          <td class="cell-nowrap">${escape(n.createdByName || 'Unknown')}</td>
          <td class="cell-nowrap">${n.createdAt ? escape(formatDate(n.createdAt, 'MMM d, yyyy h:mm a')) : '—'}</td>
        </tr>
      `;
    }).join('');

    const html = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>Incident Reports - ${escape(tfLabel)}</title>
          <style>
            body {
              font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
              margin: 24px;
              color: #1f2937;
            }
            .header {
              border-bottom: 2px solid #1a5f7a;
              padding-bottom: 10px;
              margin-bottom: 16px;
            }
            .header h1 {
              margin: 0 0 4px 0;
              color: #1a5f7a;
              font-size: 22px;
            }
            .header p {
              margin: 2px 0;
              font-size: 12px;
              color: #4b5563;
            }
            .summary {
              display: flex;
              gap: 12px;
              margin: 12px 0 18px;
              flex-wrap: wrap;
            }
            .summary-tile {
              flex: 1 1 140px;
              background: #f9fafb;
              border: 1px solid #e5e7eb;
              border-radius: 8px;
              padding: 10px 12px;
            }
            .summary-num {
              display: block;
              font-size: 20px;
              font-weight: 700;
              color: #0f172a;
              line-height: 1;
            }
            .summary-lbl {
              display: block;
              font-size: 10px;
              font-weight: 700;
              letter-spacing: 0.05em;
              text-transform: uppercase;
              color: #6b7280;
              margin-top: 4px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              font-size: 11px;
            }
            thead th {
              background: #f3f4f6;
              text-align: left;
              padding: 8px 10px;
              font-size: 10px;
              text-transform: uppercase;
              letter-spacing: 0.04em;
              color: #4b5563;
              border-bottom: 1px solid #e5e7eb;
            }
            tbody td {
              padding: 8px 10px;
              border-bottom: 1px solid #f1f5f9;
              vertical-align: top;
              line-height: 1.45;
            }
            tbody tr.row-clear {
              background: #f0fdf4;
            }
            .cell-nowrap { white-space: nowrap; }
            .cell-note { min-width: 280px; white-space: pre-wrap; }
            .tag, .type {
              display: inline-block;
              padding: 1px 8px;
              border-radius: 999px;
              font-size: 9px;
              font-weight: 700;
              letter-spacing: 0.04em;
              text-transform: uppercase;
              white-space: nowrap;
            }
            .tag-9am { background: #dbeafe; color: #1e40af; }
            .tag-12pm { background: #d1fae5; color: #065f46; }
            .tag-3pm { background: #ede9fe; color: #5b21b6; }
            .tag-tuesday_prayer { background: #fce7f3; color: #9d174d; }
            .tag-general { background: #f3f4f6; color: #374151; }
            .type-incident { background: #fef3c7; color: #92400e; }
            .type-clear { background: #d1fae5; color: #065f46; }
            .footer {
              margin-top: 18px;
              padding-top: 10px;
              border-top: 1px solid #e5e7eb;
              text-align: center;
              font-size: 10px;
              color: #6b7280;
            }
            @media print {
              body { margin: 14px; }
              @page { margin: 0.5in; }
              tr { page-break-inside: avoid; }
              thead { display: table-header-group; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Incident Reports</h1>
            <p>AAR Guest Management System</p>
            <p>${escape(tfLabel)} &middot; ${start.toLocaleDateString()} – ${end.toLocaleDateString()}</p>
            ${filterDescription ? `<p>Filters: ${escape(filterDescription)}</p>` : ''}
          </div>

          <div class="summary">
            <div class="summary-tile"><span class="summary-num">${filteredNotes.length}</span><span class="summary-lbl">Entries Shown</span></div>
            <div class="summary-tile"><span class="summary-num">${filteredNotes.filter(n => !isClearanceNote(n)).length}</span><span class="summary-lbl">Incidents</span></div>
            <div class="summary-tile"><span class="summary-num">${filteredNotes.filter(isClearanceNote).length}</span><span class="summary-lbl">All-clear</span></div>
            <div class="summary-tile"><span class="summary-num">${new Set(filteredNotes.map(n => n.createdByName).filter(Boolean)).size}</span><span class="summary-lbl">Distinct Volunteers</span></div>
          </div>

          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Service</th>
                <th>Type</th>
                <th>Note</th>
                <th>Logged By</th>
                <th>Logged At</th>
              </tr>
            </thead>
            <tbody>
              ${rowsHtml}
            </tbody>
          </table>

          <div class="footer">
            <p>Generated ${new Date().toLocaleString()}</p>
          </div>

          <script>
            window.onload = function() {
              window.print();
              window.onafterprint = function() { window.close(); };
            };
          </script>
        </body>
      </html>
    `;

    win.document.write(html);
    win.document.close();
  };

  if (!authChecked) {
    return (
      <div className="loading"><div className="spinner"></div><p>Loading…</p></div>
    );
  }

  return (
    <div className="incident-reports-container">
      <div className="incident-reports-header">
        <button className="btn-back" onClick={() => navigate('/admin')}>
          ← Back to Admin Panel
        </button>
        <h1 className="incident-reports-title">Incident Reports</h1>
      </div>

      {error && <div className="message error-message">{error}</div>}

      <div className="incident-reports-controls">
        <div className="control-group">
          <label htmlFor="ir-search">Search</label>
          <input
            id="ir-search"
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Keyword, volunteer name, or guest name…"
            className="ir-search-input"
          />
        </div>

        <div className="control-group">
          <label htmlFor="ir-timeframe">Timeframe</label>
          <select
            id="ir-timeframe"
            className="ir-select"
            value={timeframe}
            onChange={(e) => setTimeframe(e.target.value)}
          >
            {TIMEFRAMES.map((t) => (
              <option key={t.id} value={t.id}>{t.label}</option>
            ))}
          </select>
        </div>

        <div className="control-group">
          <label htmlFor="ir-service">Service</label>
          <select
            id="ir-service"
            className="ir-select"
            value={serviceFilter}
            onChange={(e) => setServiceFilter(e.target.value)}
          >
            <option value="all">All services</option>
            <option value="general">General / All-day</option>
            <option value="9am">Sunday 9:00 AM</option>
            <option value="12pm">Sunday 12:00 PM</option>
            <option value="3pm">Sunday 3:00 PM</option>
            <option value="tuesday_prayer">Tuesday Prayer</option>
          </select>
        </div>

        <div className="control-group ir-tabs">
          <span className="control-label">Showing</span>
          <div className="ir-tab-group" role="tablist">
            {FILTERS.map((f) => (
              <button
                key={f.id}
                type="button"
                className={`ir-tab${filterMode === f.id ? ' active' : ''}`}
                onClick={() => setFilterMode(f.id)}
                role="tab"
                aria-selected={filterMode === f.id}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        <div className="control-group ir-export">
          <span className="control-label">Export</span>
          <div className="ir-export-actions">
            <button
              type="button"
              className="btn-export-csv"
              onClick={exportCSV}
              disabled={filteredNotes.length === 0}
            >
              CSV
            </button>
            <button
              type="button"
              className="btn-export-pdf"
              onClick={exportPDF}
              disabled={filteredNotes.length === 0}
            >
              PDF
            </button>
          </div>
        </div>
      </div>

      <div className="ir-summary">
        <div className="ir-summary-tile">
          <span className="ir-summary-number">{stats.total}</span>
          <span className="ir-summary-label">Total Entries</span>
        </div>
        <div className="ir-summary-tile">
          <span className="ir-summary-number">{stats.incidents}</span>
          <span className="ir-summary-label">Incidents</span>
        </div>
        <div className="ir-summary-tile">
          <span className="ir-summary-number">{stats.clearances}</span>
          <span className="ir-summary-label">All-clear Entries</span>
        </div>
        <div className="ir-summary-tile">
          <span className="ir-summary-number">{stats.authors}</span>
          <span className="ir-summary-label">Distinct Volunteers</span>
        </div>
      </div>

      {loading ? (
        <div className="ir-loading"><div className="spinner"></div><p>Loading entries…</p></div>
      ) : filteredNotes.length === 0 ? (
        <div className="ir-empty">
          {notes.length === 0
            ? 'No incident notes recorded for this period.'
            : 'No entries match the current filters.'}
        </div>
      ) : (
        <div className="ir-table-wrapper" role="region" aria-label="Incident reports">
          <table className="ir-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Service</th>
                <th>Type</th>
                <th>Note</th>
                <th>Logged By</th>
                <th>Logged At</th>
              </tr>
            </thead>
            <tbody>
              {filteredNotes.map((n) => {
                const clearance = isClearanceNote(n);
                return (
                  <tr key={n.id} className={clearance ? 'row-clearance' : 'row-incident'}>
                    <td className="cell-date">{n.serviceDate || '—'}</td>
                    <td>
                      <span className={`ir-tag tag-${n.serviceType || 'general'}`}>
                        {getServiceLabel(n.serviceType)}
                      </span>
                    </td>
                    <td>
                      <span className={`ir-type ${clearance ? 'type-clearance' : 'type-incident'}`}>
                        {clearance ? 'All-clear' : 'Incident'}
                      </span>
                    </td>
                    <td className="cell-note">{n.noteText}</td>
                    <td className="cell-author">{n.createdByName || 'Unknown'}</td>
                    <td className="cell-time">
                      {n.createdAt ? formatDate(n.createdAt, 'MMM d, yyyy h:mm a') : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <div className="ir-mobile-list">
            {filteredNotes.map((n) => {
              const clearance = isClearanceNote(n);
              return (
                <div key={n.id} className={`ir-mobile-card${clearance ? ' card-clearance' : ''}`}>
                  <div className="ir-mobile-row-top">
                    <span className={`ir-tag tag-${n.serviceType || 'general'}`}>
                      {getServiceLabel(n.serviceType)}
                    </span>
                    <span className={`ir-type ${clearance ? 'type-clearance' : 'type-incident'}`}>
                      {clearance ? 'All-clear' : 'Incident'}
                    </span>
                  </div>
                  <p className="ir-mobile-text">{n.noteText}</p>
                  <div className="ir-mobile-meta">
                    <span><strong>{n.createdByName || 'Unknown'}</strong></span>
                    <span>{n.serviceDate || '—'}</span>
                  </div>
                  <div className="ir-mobile-meta">
                    <span className="ir-mobile-time">
                      {n.createdAt ? formatDate(n.createdAt, 'MMM d, yyyy h:mm a') : '—'}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default IncidentReports;
